<script setup lang="ts">
import { ref, computed } from 'vue';
import { arrayHelpers, GenericSelector, SelectOption } from '@hfc/ui-core';
import { Field, useForm } from 'vee-validate';
import { SmallBusinessContribution } from '@/application/smallBusiness/domain/SmallBusinessContribution';
import { useSmallBusinessStore } from '@/application/smallBusiness/infrastructure/composition/useSmallBusinessStore';

const { smallBusinessStore, smallBusinessState } = useSmallBusinessStore();

const { handleSubmit } = useForm();
const { generateRangeOptions } = arrayHelpers();
const runValidations = handleSubmit(() => true);

const employeeContributionFieldRef = ref();
const dependentContributionFieldRef = ref();

const contributionRules = 'between:50,100';
const dependentContributionRules = 'between:0,100';

const employeeContributionOptions = generateRangeOptions(5, 50, 100);

const dependentContributionOptions = generateRangeOptions(5, 0, 100);

const localEmployeeContribution = computed({
  get: () => getSelectedValue(smallBusinessState.value.application.contribution?.employee_amount, employeeContributionOptions),
  set: (val: Array<SelectOption> | SelectOption | undefined) => {
    updateEmployerContribution({
      employee_amount: Number((val as SelectOption).value)
    });
  }
});

const localDependentContribution = computed({
  get: () => getSelectedValue(smallBusinessState.value.application.contribution?.dependent_amount, dependentContributionOptions),
  set: (val: Array<SelectOption> | SelectOption | undefined) => {
    updateEmployerContribution({
      dependent_amount: Number((val as SelectOption).value)
    });
  }
});

const updateEmployerContribution = async (contribution: Partial<SmallBusinessContribution>): Promise<void> => {
  const isValid = await runValidations();
  await smallBusinessStore.setContribution(contribution);

  if (isValid) {
    await smallBusinessStore.fetchAvailablePlans();
  }
};

const getSelectedValue = (value: number, options: Array<SelectOption>): SelectOption | undefined => {
  return options.find((option) => option.value === String(value)) || options[0];
};
</script>
<template>
  <div class="flex w-48 flex-col justify-between">
    <div class="flex mb-4">
      <div>Employer Contribution</div>
    </div>
    <div class="flex flex-col">
      <Field :model-value="localEmployeeContribution?.value" type="number" v-slot="{ errorMessage }" name="employeeContribution" :rules="contributionRules" ref="employeeContributionFieldRef">
        <div class="flex flex-row items-center justify-between">
          <div class="text-sm mr-2 whitespace-nowrap">For Employees</div>
          <div class="w-20">
            <GenericSelector
              v-model="localEmployeeContribution"
              :options="employeeContributionOptions"
              :searchable="true" />
          </div>
        </div>
        <span class="error">{{ errorMessage }}</span>
      </Field>
    </div>
    <div class="flex flex-col">
      <Field :model-value="localDependentContribution?.value" type="number" v-slot="{ errorMessage }" name="dependentContribution" :rules="dependentContributionRules" ref="dependentContributionFieldRef">
        <div class="flex flex-row items-center justify-between">
          <div class="text-sm mr-2 whitespace-nowrap">For Dependents</div>
          <div class="w-20">
            <GenericSelector
              v-model="localDependentContribution"
              :options="dependentContributionOptions"
              :searchable="true" />
          </div>
        </div>
        <span class="error">{{ errorMessage }}</span>
      </Field>
    </div>
  </div>
</template>
